import { AsyncStateStatus } from '@sonar-software/xuder';
import { ButtonProps } from 'antd/lib';
import { ConfirmDelete } from '@ui/modals';
import { createAsyncSelectors } from '@lib/selectors';
import { DropdownButtonProps } from 'antd/es/dropdown';
import { FullUserBoundary } from '@ui/general';
import { getClosestCardBody } from '@lib/popups';
import { getQueryString } from '@sonar-software/sonar-http';
import { useNavigate } from 'react-router';

import { Button, Dropdown } from 'antd';
import { COPY_SPECIFIC, FULL_ROUTES, MESSAGES_COPY } from '@sonar-software/sonar-ts-constants';
import { coreActions, DataSecurityApplicationStatus, FeatureFlag } from '@sonar-software/sonar-core';
import React, { useCallback } from 'react';
import { useAction, useAsyncProcessing, useFeatureFlag } from '@hooks';

const detectedStatus = DataSecurityApplicationStatus[ DataSecurityApplicationStatus.Detected ].toLowerCase();
const hiddenStatus = DataSecurityApplicationStatus[ DataSecurityApplicationStatus.Deleted ].toLowerCase();

export const unhideApplicationSelectors = createAsyncSelectors(
	(state) => state.core.dataSecurity.applications.unhide
);

export const hideApplicationSelectors = createAsyncSelectors(
	(state) => state.core.dataSecurity.applications.hide
);

const dropdownButtonProps: DropdownButtonProps = {
	buttonsRender: ([ leftButton, rightButton ]) => [
		<div key='leftButton'>{ leftButton }</div>,
		<div key='rightButton'>
			<FullUserBoundary>
				{ rightButton }
			</FullUserBoundary>
		</div>
	],
	className: 'investigate-menu',
	placement: 'bottomRight'
};

export interface DetectedApplicationActionsProps {
	applicationId: number;
	onStatusChange: (status: string) => void;
	status: string;
}

export const DetectedApplicationActions: React.FC<DetectedApplicationActionsProps> = ({
	applicationId,
	onStatusChange,
	status
}) => {
	const navigate = useNavigate();
	const allowOverview = useFeatureFlag(FeatureFlag.DataSecurityDashboard);

	const hideApplication = useAction(coreActions.requestDeleteCoreDataSecurityApplications);
	const unhideApplication = useAction(coreActions.requestPatchCoreDataSecurityApplications);
	const resetHideApplication = useAction(coreActions.resetDeleteCoreDataSecurityApplications);
	const resetUnhideApplication = useAction(coreActions.resetPatchCoreDataSecurityApplications);

	const unhideProcess = useAsyncProcessing({
		resetAction: resetUnhideApplication,
		stateSelector: unhideApplicationSelectors.state,
		statusHandlers: {
			[ AsyncStateStatus.Received ]: () => onStatusChange(detectedStatus)
		}
	});

	const hideProcess = useAsyncProcessing({
		resetAction: resetHideApplication,
		stateSelector: hideApplicationSelectors.state,
		statusHandlers: {
			[ AsyncStateStatus.Received ]: () => onStatusChange(hiddenStatus)
		}
	});

	const handleUnhide = useCallback(() => {
		unhideProcess.watchNextCall();

		unhideApplication({ applicationId });
	}, [ applicationId, unhideProcess, unhideApplication ]);

	const handleHide = useCallback(() => {
		hideProcess.watchNextCall();

		hideApplication({ applicationId });
	}, [ applicationId, hideProcess, hideApplication ]);

	const isHidden = status === hiddenStatus;

	if (isHidden) {
		return (
			<FullUserBoundary>
				<Button
					onClick={ handleUnhide }
					size='small'>
					{ COPY_SPECIFIC.UNHIDE }
				</Button>
			</FullUserBoundary>
		);
	}

	const investigateButtonProps: ButtonProps = {
		children: COPY_SPECIFIC.INVESTIGATE,
		onClick: () => navigate(`${FULL_ROUTES.DATA_SECURITY}/${applicationId}`)
	};

	const monitorEntry = {
		key: 'monitor',
		label: COPY_SPECIFIC.MONITOR_APP,
		onClick: () => navigate(`${FULL_ROUTES.ADD_APP}?${getQueryString({ applicationId })}`)
	};

	const hideEntry = {
		key: 'hide',
		label:
	<ConfirmDelete
		className='confirm-hide'
		confirmButtonProps={ {
			value: COPY_SPECIFIC.HIDE
		} }
		getPopupContainer={ getClosestCardBody }
		onConfirm={ handleHide }
		title={ MESSAGES_COPY.ARE_YOU_SURE_INTEGRATION_APP }>
		{ COPY_SPECIFIC.HIDE }
	</ConfirmDelete>
	};

	if (allowOverview) {
		return (
			<Dropdown.Button
				getPopupContainer={ getClosestCardBody }
				{ ...dropdownButtonProps }
				{ ...investigateButtonProps }
				menu={ {
					items: [ monitorEntry, hideEntry ]
				} } />
		);
	}

	return (
		<Dropdown.Button
			getPopupContainer={ getClosestCardBody }
			{ ...dropdownButtonProps }
			menu={ {
				items: [ hideEntry ]
			} }
			onClick={ monitorEntry.onClick }>
			{ monitorEntry.label }
		</Dropdown.Button>
	);
};
